
import { ref, computed, defineComponent, PropType, onMounted, onUnmounted, watch, WritableComputedRef } from 'vue';
import ATable, { ColumnGroupType, ColumnType, TablePaginationConfig } from 'ant-design-vue/es/table';
import ruRU from 'ant-design-vue/es/locale/ru_RU';
import AConfigProvider from 'ant-design-vue/es/config-provider';
import { AlignType, GetComponentProps, DefaultRecordType } from 'ant-design-vue/es/vc-table/interface';
import { useRouter } from 'vue-router';

const SCREEN_LAPTOP_BREACKPOINT = 1024;

export default defineComponent({
  inheritAttrs: false,
  components: {
    ATable,
    ATableSummaryRow: ATable.Summary.Row,
    ATableSummaryCell: ATable.Summary.Cell,
    AConfigProvider,
  },
  props: {
    columns: {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      type: Array as PropType<(ColumnGroupType<any> | ColumnType<any>)[]>,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    summary: {
      type: Array as PropType<TableItem[]>,
      default: () => [],
    },
    pagination: {
      type: Object as PropType<TablePaginationConfig>,
      required: false,
    },
    rowLink: Function,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    customRow: Object as PropType<GetComponentProps<any>>,
    showTotal: Function as PropType<((total: number, range: [number, number]) => string) | undefined>,
    scrollY: [Number, String],
    expandedRowKeys: {
      type: Array as PropType<(string | number)[]>,
      required: false,
    },
  },
  emits: ['update:expandedRowKeys', 'expand'],
  setup(props, { emit }) {
    const rowSelectable = computed(() => props.customRow || props.rowLink);
    const router = useRouter();

    const customRowProxy = computed(() => {
      if (props.rowLink) {
        return (record: DefaultRecordType) => ({
          onClick: () => router.push(props.rowLink?.(record)),
          onMouseup: (event: MouseEvent) => {
            if (event.button === 1) {
              const url = router.resolve(props.rowLink?.(record));
              window.open(url.href, '_blank');
            }
          },
          onMousedown: (event: MouseEvent) => {
            if (event.button === 1) {
              event.preventDefault();
              return false;
            }
            return true;
          },
        });
      }
      return props.customRow || (() => ({}));
    });

    const windowWidth = ref(window.innerWidth);
    const updateWindowWidth = () => (windowWidth.value = window.innerWidth);
    onMounted(() => window.addEventListener('resize', updateWindowWidth));
    onUnmounted(() => window.removeEventListener('resize', updateWindowWidth));

    // Управляемое раскрытие строк
    const internalExpandedRowKeys = ref<Array<string | number>>([]);
    const expandedRowKeysProxy: WritableComputedRef<Array<string | number> | undefined> = computed({
      get: () => (props.expandedRowKeys !== undefined ? internalExpandedRowKeys.value : undefined),
      set: (val) => {
        if (val) {
          internalExpandedRowKeys.value = val;
          emit('update:expandedRowKeys', val);
        }
      },
    });

    watch(
      () => props.expandedRowKeys,
      (val) => {
        if (val) {
          expandedRowKeysProxy.value = val;
        }
      },
    );

    const onExpand = (expanded: boolean, record: DefaultRecordType) => {
      if (!expandedRowKeysProxy.value) {
        emit('expand', expanded, record);
        return;
      }

      const key = record.key;
      expandedRowKeysProxy.value = expanded
        ? [...expandedRowKeysProxy.value, key]
        : expandedRowKeysProxy.value.filter((k) => k !== key);

      emit('expand', expanded, record);
    };

    return {
      localeRu: ruRU,
      rowSelectable,
      customRowProxy,
      expandedRowKeysProxy,
      onExpand,
      isHorizontalScrollActive: computed(() => (windowWidth.value <= SCREEN_LAPTOP_BREACKPOINT ? true : undefined)),
    };
  },
});

export type TableItem = {
  title: string | number;
  dataIndex: string | number;
  align?: AlignType | undefined;
};
